import * as React from "react";
import * as ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";
import "./index.css";
import { muiTheme } from "./theme";
import { Layout } from "./layouts";
import { ThemeProvider } from "@emotion/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import Login from "./pages/Login/Login";
import { ErrorDisplayProvider } from "./contexts/ErrorDisplayContext";
import "./axiosSetup";
import {
  getJarvis2RedirectUrl,
  redirectToJarvis2IfNeeded,
} from "./jarvis2Redirect";

/**
 * Integration pages moved to Jarvis 2.0.
 * This app only keeps Login + redirect for old email/OAuth links.
 */
function RequireAuth({ children }: { children: JSX.Element }) {
  const [ready, setReady] = React.useState(false);
  const [user, setUser] = React.useState<any>(null);
  React.useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u);
        setReady(true);
      }),
    []
  );
  if (!ready) return null;
  if (!user)
    return (
      <Navigate
        to={`/login?next=${encodeURIComponent(
          window.location.pathname + window.location.search
        )}`}
        replace
      />
    );
  return children;
}

function MovedToJarvis2() {
  const target = getJarvis2RedirectUrl(
    window.location.pathname,
    window.location.search
  );

  React.useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);

  if (target) return null;

  return (
    <div style={{ padding: 48, fontFamily: "Inter, Arial, sans-serif" }}>
      <h1>This page has moved to Jarvis 2.0</h1>
      <p>
        Set <code>REACT_APP_JARVIS2_HOST</code> to enable automatic redirects,
        or open the Jarvis 2.0 app directly.
      </p>
    </div>
  );
}

const router = createBrowserRouter([
  { path: "/login", element: <Login /> },
  {
    path: "*",
    element: (
      <RequireAuth>
        <MovedToJarvis2 />
      </RequireAuth>
    ),
  },
]);

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const queryClient = new QueryClient();

if (redirectToJarvis2IfNeeded()) {
  root.render(null);
} else {
  root.render(
    <ThemeProvider theme={muiTheme}>
      <QueryClientProvider client={queryClient}>
        <ErrorDisplayProvider>
          <Layout>
            <RouterProvider router={router} />
          </Layout>
        </ErrorDisplayProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
